// Mount the Business package (#212): build its routes from what it needs from
// this app — the generic host (modals, the signed-in user and permissions:
// host-base.ts), the initialised i18n instance (it registers its `business`
// namespace there), the Excel export, the customer and employee selects, the
// entity lookups, the date presets, the error page (as each route's errorElement) and the pages of another
// module it links to. App.tsx mounts the routes from HERE, so the package
// cannot be mounted unregistered. configs/i18n is imported (and so
// initialised) before the registration runs, whatever order main.tsx imports
// things in. The HTTP client goes through core's registry
// (utils/axios/register-core.ts).
import { ExcelExportButton } from '@/components/ExcelExportButton'
import {
    createBusinessModule,
    type BusinessHost,
} from '@vnetwork/datacentral-business'
import { hostBase } from '@/app/host-base'
import i18n from '@/configs/i18n'
import ErrorPage from '@/components/ErrorBoundary/ErrorPage'
import { lazy, Suspense } from 'react'
import { paths } from '@/utils/path'
import { DATE_RANGE_PRESETS } from '@/utils/table'

// Lazy: the two selects only render on the create page. Imported eagerly here
// they pull their loaders into the entry chunk; before the move they loaded
// with the Business route chunk.
const LazyAsyncSelectWithServer = lazy(
    () => import('@/components/Form/Shared/AsyncSelectWithServer')
)
const LazyEmployeeSelect = lazy(
    () => import('@/components/Form/Shared/EmployeeSelect')
)
const AsyncSelectWithServer: BusinessHost['AsyncSelectWithServer'] = (
    props
) => (
    <Suspense fallback={null}>
        <LazyAsyncSelectWithServer {...props} />
    </Suspense>
)
const EmployeeSelect: BusinessHost['EmployeeSelect'] = (props) => (
    <Suspense fallback={null}>
        <LazyEmployeeSelect {...props} />
    </Suspense>
)

// Lazy: SimpleAsyncSelect/config imports the employee, customer, department,
// position and ticket services. Imported eagerly here it would land in the
// entry chunk (+66 KiB, measured in #204); before the move it loaded with the
// Business route chunk. Each call loads it on first use, when a filter opens.
const loadEntityConfig = (entity: 'department' | 'position' | 'employee') =>
    import('@/components/Form/Shared/SimpleAsyncSelect/config').then((m) =>
        m.getEntityConfig(entity)
    )
const getEntityConfig: BusinessHost['getEntityConfig'] = (entity) => ({
    fetchOptions: async (query, params) =>
        (await loadEntityConfig(entity)).fetchOptions(query, params),
    fetchOptionsWithPage: async (query, page, params) =>
        (await loadEntityConfig(entity)).fetchOptionsWithPage(
            query,
            page,
            params
        ),
    fetchByIds: async (ids, params) =>
        (await loadEntityConfig(entity)).fetchByIds(ids, params),
})

// The package does not render the app's ErrorPage (unlike Dayoffs' update
// page); the host adds it as each route's errorElement, as for Warehouse (#59)
// and Dayoffs (#204).
export const BusinessRoutes = createBusinessModule({
    ...hostBase,
    i18n,
    // customerNew is the first link to another module that used to be a string
    // literal inside the package ('/customers/new?...'): it is named here now,
    // so a renamed route fails typecheck in this file, not at a click.
    links: {
        account: paths.account,
        customerDetail: paths.customerDetail,
        customerNew: paths.customerCreate,
    },
    ExcelExportButton,
    AsyncSelectWithServer,
    EmployeeSelect,
    getEntityConfig,
    dateRangePresets: DATE_RANGE_PRESETS,
}).map((route) => ({ ...route, errorElement: <ErrorPage status={500} /> }))
